import { Photo } from '../types/photoAlbum';
import Sponsor from '../types/sponsor';

export type AlbumGridCell = { kind: 'photo'; photo: Photo } | { kind: 'sponsor'; sponsor: Sponsor };

export type AlbumGridRow = {
  key: string;
  cells: AlbumGridCell[];
};

const SPONSOR_SPAN = 2;

function cellSpan(cell: AlbumGridCell): number {
  return cell.kind === 'sponsor' ? SPONSOR_SPAN : 1;
}

function packRows(cells: AlbumGridCell[], columns: number): AlbumGridRow[] {
  const rows: AlbumGridRow[] = [];
  let current: AlbumGridCell[] = [];
  let used = 0;

  cells.forEach((cell) => {
    const span = cellSpan(cell);
    if (used > 0 && used + span > columns) {
      rows.push({ key: `row-${rows.length}`, cells: current });
      current = [];
      used = 0;
    }
    current.push(cell);
    used += span;
    if (used >= columns) {
      rows.push({ key: `row-${rows.length}`, cells: current });
      current = [];
      used = 0;
    }
  });

  if (current.length > 0) {
    rows.push({ key: `row-${rows.length}`, cells: current });
  }

  return rows;
}

function buildCells(photos: Photo[], sponsors: Sponsor[]): AlbumGridCell[] {
  if (photos.length === 0) {
    return sponsors.map((sponsor) => ({ kind: 'sponsor' as const, sponsor }));
  }

  const cells: AlbumGridCell[] = [];
  let sponsorIdx = 0;

  photos.forEach((photo, i) => {
    cells.push({ kind: 'photo', photo });
    const shouldInsertSponsor = sponsorIdx < sponsors.length && (i === 2 || (i > 2 && (i - 2) % 6 === 0));
    if (shouldInsertSponsor) {
      cells.push({ kind: 'sponsor', sponsor: sponsors[sponsorIdx] });
      sponsorIdx += 1;
    }
  });

  return cells.concat(sponsors.slice(sponsorIdx).map((sponsor) => ({ kind: 'sponsor' as const, sponsor })));
}

/**
 * Mixes sponsors into the photo grid as 2×1 tiles and packs them into rows
 * for the given column count (so flex/grid tracks stay aligned).
 */
export function buildAlbumGridRows(photos: Photo[], sponsors: Sponsor[], columns: number): AlbumGridRow[] {
  return packRows(buildCells(photos, sponsors), Math.max(columns, SPONSOR_SPAN));
}

/** Flat list (e.g. when column count is handled by CSS only). */
export function buildAlbumGridItems(photos: Photo[], sponsors: Sponsor[]): AlbumGridCell[] {
  return buildCells(photos, sponsors);
}
